import { Add } from "@mui/icons-material";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { theme } from "../../../Theme/theme";
import { GET, PATCH, POST } from "../../../Utilities/BaseService";
import SearchableInput from "../../Dashboard/Components/SearchableInput";
import SearchBar from "../../Table/Components/SearchBar";

/* ---------------- API ---------------- */

const getDistricts = () => GET("/region/district/", { limit: 0 });
const addVillage = (village: any) => POST("/region/village", village);
const updateVillage = ({ village, id }: any) =>
    PATCH("/region/village", village, { id });

/* ---------------- COMPONENT ---------------- */

const AddVillage = ({ updateMode }: any) => {
    const [id, setId] = useState("");
    const [village, setVillage] = useState({
        name: "",
        district: "",
        powerstations: [] as number[],
    });

    const [powerstationData, setPowerstationData] = useState<any[]>([]);
    const [filter, setFilter] = useState({ district: "", search: "" });
    const [feedback, setFeedback] = useState("");

    /* pagination */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);

    const location = useLocation();

    useQuery(["districts"], getDistricts);

    /* ---------------- UPDATE MODE ---------------- */

    const getVillageById = () => GET(`/region/village?id=${id}`);

    const { data: villageById } = useQuery(["village", id], getVillageById, {
        enabled: Boolean(id),
        onSuccess(res) {
            const v = res.data.rows[0];
            if (!v) return;

            setVillage({
                name: v.name,
                district: v.district.id,
                powerstations:
                    v.villagePowerstation?.map((p: any) => p.powerstation.id) ||
                    [],
            });

            setFilter({ district: v.district.id, search: "" });
        },
    });

    useEffect(() => {
        if (!updateMode) return;
        const params = new URLSearchParams(location.search);
        const _id = params.get("id");
        if (_id) setId(_id);
    }, [updateMode]);

    /* ---------------- FETCH POWERSTATIONS ---------------- */
    const getPowerStationData = async (
        districtId: string,
        page: number,
        limit: number
    ) => {
        const queryParams = [
            districtId ? `district=${districtId}` : null,
            filter.search
                ? `search=${encodeURIComponent(filter.search)}`
                : null,
            `page=${page + 1}`,
            `limit=${limit}`,
        ]
            .filter(Boolean) // remove nulls
            .join("&"); // join with &

        const res = await GET(`/powerstation?${queryParams}`);

        setPowerstationData(res.data.rows || []);
        setTotal(res.data.count || 0);
    };

    useEffect(() => {
        getPowerStationData(filter.district, page, rowsPerPage);
    }, [filter.district, page, rowsPerPage, filter.search]);

    /* ---------------- CHECKBOX LOGIC ---------------- */

    const handlePowerstationChange = (psId: number) => {
        setVillage((prev) => ({
            ...prev,
            powerstations: prev.powerstations.includes(psId)
                ? prev.powerstations.filter((id) => id !== psId)
                : [...prev.powerstations, psId],
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        const currentPageIds = powerstationData.map((ps) => ps.id);

        setVillage((prev) => ({
            ...prev,
            powerstations: checked
                ? Array.from(
                      new Set([...prev.powerstations, ...currentPageIds])
                  )
                : prev.powerstations.filter(
                      (id) => !currentPageIds.includes(id)
                  ),
        }));
    };

    const selectedCount = powerstationData.filter((ps) =>
        village.powerstations.includes(ps.id)
    ).length;

    const allSelected =
        powerstationData.length > 0 &&
        selectedCount === powerstationData.length;

    const indeterminate =
        selectedCount > 0 && selectedCount < powerstationData.length;

    /* ---------------- SUBMIT ---------------- */

    const addMutation = useMutation(addVillage, {
        onSuccess(res) {
            setFeedback(res.data.message);
        },
        onError(err: any) {
            setFeedback(err.response?.data?.message || "Error");
        },
    });

    const updateMutation = useMutation(updateVillage, {
        onSuccess(res) {
            setFeedback(res.data.message);
        },
        onError(err: any) {
            setFeedback(err.response?.data?.message || "Error");
        },
    });

    const handleSubmit = (e: any) => {
        e.preventDefault();
        updateMode
            ? updateMutation.mutate({ village, id })
            : addMutation.mutate(village);
    };

    useEffect(() => {
        setVillage((prev) => {
            const isSameDistrict =
                updateMode &&
                villageById?.data.rows[0]?.district?.id === filter.district;

            return {
                ...prev,
                district: filter.district,
                powerstations: isSameDistrict
                    ? villageById?.data.rows[0]?.villagePowerstation?.map(
                          (p: any) => p.powerstation.id
                      ) || []
                    : [],
            };
        });

        setPage(0); // reset pagination
    }, [filter.district, updateMode, villageById]);

    /* ---------------- RENDER ---------------- */

    return (
        <>
            <Card sx={{ bgcolor: theme.palette.common.white }}>
                <CardHeader
                    title={updateMode ? "Update Village" : "Add Village"}
                />

                <CardContent>
                    <Divider sx={{ mb: 3 }} />

                    <Grid
                        container
                        component="form"
                        onSubmit={handleSubmit}
                        spacing={1}
                    >
                        <Grid item xs={12} md={6}>
                            <TextField
                                size="small"
                                label="Village Name"
                                fullWidth
                                required
                                value={village.name}
                                onChange={(e) =>
                                    setVillage({
                                        ...village,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <SearchableInput
                                label="District"
                                api="/region/district"
                                filter={filter}
                                setFilter={setFilter}
                                multiple={false}
                                defaultValue={
                                    updateMode &&
                                    villageById?.data.rows[0].district
                                }
                            />
                        </Grid>

                        {/* ---------------- POWERSTATIONS TABLE ---------------- */}

                        <Grid item xs={12}>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                style={{ paddingBottom: "1rem" }}
                            >
                                <SearchBar
                                    filter={filter}
                                    setFilter={setFilter}
                                />
                            </Grid>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={indeterminate}
                                                onChange={(e) =>
                                                    handleSelectAll(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>Name</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {powerstationData.map((ps) => (
                                        <TableRow key={ps.id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={village.powerstations.includes(
                                                        ps.id
                                                    )}
                                                    onChange={() =>
                                                        handlePowerstationChange(
                                                            ps.id
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>{ps.name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[5, 10, 25]}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(
                                        parseInt(e.target.value, 10)
                                    );
                                    setPage(0);
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="outlined"
                                color="secondary"
                                startIcon={<Add />}
                                endIcon={
                                    addMutation.isLoading ||
                                    updateMutation.isLoading ? (
                                        <CircularProgress size={16} />
                                    ) : null
                                }
                            >
                                {updateMode ? "Update" : "Add"} Village
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {(addMutation.isSuccess || updateMutation.isSuccess) && (
                <Alert severity="success">{feedback}</Alert>
            )}
            {(addMutation.isError || updateMutation.isError) && (
                <Alert severity="error">{feedback}</Alert>
            )}
        </>
    );
};

export default AddVillage;
